import React, { useEffect, useState } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'
import { Select } from '@material-ui/core'
import { MenuItem } from 'material-ui'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import MomentUtils from '@date-io/moment-v1'
import { styles } from './styles'
import GenericFlatButton from '../../../../../../../../Components/GenericFlatButton'
import {
  CONTRACT_TYPES,
  CONTRACT_TYPE_TO_NAME_MAP,
} from '../../../../../../../../shared/constants'
import { hoursPerWeekOptions } from '../../const'
import {
  calculateDiffInBusinessDaysFromToday,
  germanHolidays,
} from '../../utils'
import { getBADateOfBirth } from '../../../../../../../../Utils/User'
import { CONFIG } from '../../../../../../../../Config/config'

const ChangeContractDataModal = ({
  data,
  onSubmit,
  onCancel,
  title,
  subtitle,
  ambassador,
  classes,
}) => {
  const [type, setType] = useState(CONTRACT_TYPES[data.contractType])
  const [startDate, setStartDate] = useState(moment(data.startDate))
  const [endDate, setEndDate] = useState(
    data.endDate ? moment(data.endDate) : null,
  )
  const [hoursPerWeek, setHoursPerWeek] = useState(data.hoursPerWeek)
  const [isInputsDisabled, setIsInputsDisabled] = useState(false)
  const [alertProps, setAlertProps] = useState({ visible: false })
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    if (
      type == CONTRACT_TYPES.GERMANY_PART_TIME &&
      data.currentEmploymentStatus == 'Student'
    ) {
      setIsInputsDisabled(true)
      setAlertProps({
        visible: true,
        title:
          'Are you sure you want to create a part-time contract for a student? This may have tax implications. Please confirm if you want to proceed.',
        onDiscard: () => setType(null),
        onOkay: () => {
          setIsInputsDisabled(false)
          setAlertProps({ visible: false })
        },
      })
    }
  }, [])

  const checkForStudentContractIssue = contractType => {
    if (
      contractType == CONTRACT_TYPES.GERMANY_PART_TIME &&
      data.currentEmploymentStatus == 'Student'
    )
      return true
  }

  const validateStartEndDate = () => {
    setValidationErrors({})
    if (startDate && endDate) {
      const differenceInDays = endDate.diff(startDate, 'days')
      if (differenceInDays < 7) {
        setValidationErrors({
          startDate: 'Contract duration can’t be less than 1 week',
          endDate: 'Contract duration can’t be less than 1 week',
        })
      }
    }
    if (startDate.diff(moment(), 'days') < 0) {
      setValidationErrors({
        startDate: `The start of the contract can't be from the past`,
      })
    }

    if (type == CONTRACT_TYPES.GERMANY_SHORT_TERM && endDate) {
      const maxEndDate = moment(startDate)
        .add(3, 'month')
        .add(1, 'days')
      const isEndDateCorrect = moment(endDate).isSameOrBefore(maxEndDate)
      if (!isEndDateCorrect) {
        setValidationErrors({
          endDate:
            'The end date must be within 3 months of the start date (inclusive)',
        })
      }
    }
  }

  const form = {
    contractType: type,
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
    hoursPerWeek,
  }
  useEffect(() => {
    validateStartEndDate()
  }, [startDate, endDate])

  useEffect(() => {
    setEndDate(null)
  }, [startDate, type])

  const onValueChange = (callback, title) => {
    setIsInputsDisabled(true)
    setAlertProps({
      visible: true,
      title,
      onOkay: () => {
        callback()
        setIsInputsDisabled(false)
        setAlertProps({ visible: false })
      },
    })
  }

  const contractTypeOptions = [
    {
      label: CONTRACT_TYPE_TO_NAME_MAP.GERMANY_WORKING_STUDENT,
      value: CONTRACT_TYPES.GERMANY_WORKING_STUDENT,
    },
    {
      label: CONTRACT_TYPE_TO_NAME_MAP.GERMANY_PART_TIME,
      value: CONTRACT_TYPES.GERMANY_PART_TIME,
    },
    {
      label: CONTRACT_TYPE_TO_NAME_MAP.GERMANY_SHORT_TERM,
      value: CONTRACT_TYPES.GERMANY_SHORT_TERM,
    },
    {
      label: CONTRACT_TYPE_TO_NAME_MAP.GERMANY_MARGINAL_PART_TIME,
      value: CONTRACT_TYPES.GERMANY_MARGINAL_PART_TIME,
    },
  ]

  const hoursToContractOptionsMap = {
    '1': hoursPerWeekOptions.slice(0, 5),
    '2': hoursPerWeekOptions,
    '3': hoursPerWeekOptions.slice(0, 2),
    '4': hoursPerWeekOptions.slice(0, 2),
    null: hoursPerWeekOptions,
  }

  const hasValidationErrors =
    validationErrors.startDate || validationErrors.endDate
  const hasWrongHoursPerWeek = type
    ? !hoursPerWeek ||
      !hoursToContractOptionsMap[type].find(opt => opt.value == hoursPerWeek)
    : true

  const generateStartDateWarning = startDate => {
    const diff = calculateDiffInBusinessDaysFromToday(startDate)
    if (diff >= 1 && diff <= 3) {
      return 'Are you sure you want to select a date earlier than 4 working days from today?'
    }
    return `Are you sure you want to change start date?`
  }

  const disableWeekends = date => {
    const day = date.day()
    const isHolyday = germanHolidays.includes(date.format('YYYY-MM-DD'))
    return day === 0 || isHolyday
  }

  return (
    <div className={classes.container}>
      {title && <div className={classes.title}>{title}</div>}
      {subtitle && <div className={classes.title}>{subtitle}</div>}
      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Contract type:</div>
        <div>
          <div className={classes.select}>
            <Select
              disableUnderline
              disabled={isInputsDisabled}
              value={type}
              onChange={event => {
                setHoursPerWeek(null)
                onValueChange(
                  () => setType(event.target.value),
                  checkForStudentContractIssue(event.target.value)
                    ? 'Are you sure you want to create a part-time contract for a student? This may have tax implications. Please confirm if you want to proceed.'
                    : `Are you sure you want to change contract type?`,
                )
              }}
              style={{
                width: 270,
                height: 36,
                border: 'none',
                paddingRight: 6,
              }}
            >
              {contractTypeOptions.map(option => (
                <MenuItem
                  key={option.label}
                  value={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 16,
                    fontFamily: "'Proxima Nova', sans-serif",
                    backgroundColor: type == option.value ? '#E2E2FC' : 'white',
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Start date:</div>
        <div className={classes.datePicker}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <KeyboardDatePicker
              disableToolbar
              inputProps={{ readOnly: true }}
              minDate={moment()}
              helperText={null}
              disabled={isInputsDisabled}
              variant="dialog"
              format="DD-MM-YYYY"
              margin="normal"
              id="date-picker-inline"
              value={startDate}
              shouldDisableDate={date => disableWeekends(date)}
              onChange={date =>
                onValueChange(
                  () => setStartDate(date),
                  generateStartDateWarning(date),
                )
              }
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              className={classes.inputRoot}
            />
          </MuiPickersUtilsProvider>
        </div>
      </div>
      {validationErrors.startDate && (
        <div className={classes.error}>{validationErrors.startDate}</div>
      )}

      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>End date:</div>
        <div className={classes.datePicker}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
            <KeyboardDatePicker
              disableToolbar
              inputProps={{ readOnly: true }}
              minDate={startDate}
              variant="dialog"
              disabled={isInputsDisabled}
              format="DD-MM-YYYY"
              margin="normal"
              id="date-picker-inline"
              value={endDate ? new Date(endDate) : null}
              shouldDisableDate={date => disableWeekends(date)}
              onChange={date => {
                if (!endDate) {
                  setEndDate(date)
                } else {
                  onValueChange(
                    () => setEndDate(date),
                    `Are you sure you want to change end date?`,
                  )
                }
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              className={classes.inputRoot}
            />
          </MuiPickersUtilsProvider>
        </div>
      </div>
      {validationErrors.endDate && (
        <div className={classes.error}>{validationErrors.endDate}</div>
      )}

      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Hours per week:</div>
        <div>
          <div className={classes.select}>
            <Select
              disableUnderline
              disabled={isInputsDisabled}
              value={hoursPerWeek}
              onChange={event => {
                onValueChange(
                  () => setHoursPerWeek(event.target.value),
                  `Are you sure you want to change hours per week?`,
                )
              }}
              style={{
                width: 270,
                height: 36,
                border: 'none',
                paddingRight: 6,
              }}
            >
              {hoursToContractOptionsMap[type].map(option => (
                <MenuItem
                  key={option.label}
                  value={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 16,
                    fontFamily: "'Proxima Nova', sans-serif",
                    backgroundColor:
                      hoursPerWeek == option.value ? '#E2E2FC' : 'white',
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Pay per hour, €</div>
        <div className={classes.notEditableValue}>
          {CONFIG.HARDCODED_DE_PAY_PER_HOUR}
        </div>
      </div>
      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Address</div>
        <div className={classes.notEditableValue}>{`${data.address}, ${
          data.houseNumber
        }, ${data.exactAddress ? `${data.exactAddress}, ` : ''}${data.city}, ${
          data.postalCode
        }`}</div>
      </div>
      <div className={classes.bodyContainer}>
        <div className={classes.selectTitle}>Date of birth</div>
        <div className={classes.notEditableValue}>
          {ambassador.dateOfBirth
            ? getBADateOfBirth(ambassador).format('DD.MM.YYYY')
            : '-'}
        </div>
      </div>
      {alertProps.visible && (
        <div className={classes.alertContainer}>
          <div className={classes.alertText}>{alertProps.title}</div>
          <div className={classes.alertButtonContainer}>
            <GenericFlatButton
              type={'outline'}
              title={'Discard'}
              onClick={() => {
                setIsInputsDisabled(false)
                setAlertProps({ visible: false })
                if (alertProps.onDiscard) {
                  alertProps.onDiscard()
                }
              }}
              customStyles={classes.alertButton}
            />
            <GenericFlatButton
              type={'filled'}
              title={'Confirm'}
              onClick={() => alertProps.onOkay()}
              customStyles={classes.alertButton}
            />
          </div>
        </div>
      )}
      <div className={classes.buttonContainer}>
        <GenericFlatButton
          type={'outline'}
          title={'Discard'}
          onClick={() => onCancel()}
        />
        <GenericFlatButton
          type={'filled'}
          title={'Preview'}
          onClick={async () => {
            setIsLoading(true)
            await onSubmit(form)
            setIsLoading(false)
          }}
          disabled={
            isInputsDisabled ||
            isLoading ||
            !endDate ||
            hasWrongHoursPerWeek ||
            hasValidationErrors
          }
          loading={isLoading}
        />
      </div>
    </div>
  )
}
export default injectStyles(styles)(ChangeContractDataModal)
